
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { WordPreviewScreen } from "@/components/game/screens/WordPreviewScreen";
import { LogicRuleCard } from "@/components/game/result/LogicRuleCard";
import { GameHeader } from "@/components/GameHeader";
import { LoadingSpinner } from "@/components/game/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { HomeIcon } from "lucide-react";
import { Link } from "react-router-dom";
import type { LogicRule } from "@/lib/game-utils";

export default function PlayerCard() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWords, setSelectedWords] = useState<Array<{ word: any; order: number }>>([]);
  const [selectedRule, setSelectedRule] = useState<LogicRule | null>(null);

  useEffect(() => {
    async function fetchPlayerCard() {
      try {
        if (!attemptId) {
          setError("No player card ID provided");
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('game_attempts')
          .select('selected_words, selected_rule')
          .eq('id', attemptId)
          .single();

        if (error) {
          console.error("Error fetching player card:", error);
          setError("Unable to load this player card");
          setLoading(false);
          return;
        }

        if (!data) {
          setError("Player card not found");
          setLoading(false);
          return;
        }

        // Fix type issues by properly casting the JSON data to the appropriate types
        // First cast to unknown, then to the specific type to avoid TypeScript errors
        const typedSelectedWords = data.selected_words as unknown as Array<{ word: any; order: number }>;
        
        // For LogicRule, we need to be more careful since it has specific required properties
        // Cast to unknown first, then to LogicRule to satisfy TypeScript
        const ruleData = data.selected_rule as unknown;
        
        // Verify the shape before setting state
        if (ruleData && 
            typeof ruleData === 'object' && 
            'id' in ruleData && 
            'name' in ruleData && 
            'description' in ruleData && 
            'category' in ruleData) {
          // Now it's safe to cast to LogicRule
          setSelectedRule(ruleData as LogicRule);
        } else {
          console.error("Invalid rule data structure:", ruleData);
          setError("Invalid rule data format");
          setLoading(false);
          return;
        }

        setSelectedWords(typedSelectedWords);
        setLoading(false);
      } catch (err) {
        console.error("Error in player card fetch:", err);
        setError("An unexpected error occurred");
        setLoading(false);
      }
    }

    fetchPlayerCard();
  }, [attemptId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h1 className="text-xl font-bold text-red-500 mb-4">Error</h1>
        <p className="text-gray-700 mb-6">{error}</p>
        <Link to="/">
          <Button className="flex items-center gap-2">
            <HomeIcon size={18} />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-4">
        <GameHeader />
        
        <div className="flex justify-center mb-6">
          <h1 className="text-2xl font-bold">Player Card</h1>
        </div>
        
        {selectedRule && (
          <div className="w-full max-w-md mx-auto mb-6">
            <div className="flex justify-center">
              <LogicRuleCard rule={selectedRule} />
            </div>
          </div>
        )}
        
        {selectedWords && selectedWords.length > 0 && selectedRule && (
          <WordPreviewScreen
            selectedWords={selectedWords}
            selectedRule={selectedRule}
            onRevealResults={() => {}}
            previewMode={true}
          />
        )}
        
        <div className="flex justify-center mt-8">
          <Link to="/">
            <Button className="flex items-center gap-2">
              <HomeIcon size={18} />
              Play Today's Puzzle
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
